import { useCallback, useEffect, useState } from "react";

/**
 * Bigger text, for the man who actually uses this (§11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §11.1 sets a 16px FLOOR on body text and 48px on controls, and that floor is
 * a minimum rather than a target. The owner is in his fifties, reading a phone
 * at arm's length in a workshop, often with the screen behind safety glasses or
 * covered in dust. A step above the floor is the cheapest usability win in the
 * whole app and it costs nothing to offer.
 *
 * IT SCALES `html { font-size }`, NOT A CLASS ON THE BODY. Every size in the app
 * is a Tailwind `rem` value, so moving the root font size scales type, padding,
 * gaps and — critically — the 48px touch targets together. A class that only
 * changed font sizes would make the text bigger and the buttons no easier to hit,
 * which is the opposite of the point.
 *
 * THE STYLES LIVE IN THEIR OWN FILE. `src/index.css` is generated and owned by
 * the shadcn CLI — `init` wrote it and `add` extends it — so anything hand-added
 * there is lost the next time the CLI runs. See main.tsx for the import order.
 *
 * IT IS WRITTEN TO `<html>` BEFORE REACT MOUNTS, so there is no frame at the
 * default size before the preference applies. On a slow phone that flash is
 * visible and looks like the app resetting itself.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const TEXT_SIZES = ["normal", "large", "larger"] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

export const DEFAULT_TEXT_SIZE: TextSize = "normal";

const STORAGE_KEY = "steelbill.textSize";
const ATTRIBUTE = "data-text-size";

function isTextSize(value: unknown): value is TextSize {
  return typeof value === "string" && (TEXT_SIZES as readonly string[]).includes(value);
}

/** What was chosen last time, or the default. Never throws on a locked store. */
export function getStoredTextSize(): TextSize {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isTextSize(stored) ? stored : DEFAULT_TEXT_SIZE;
  } catch {
    // Private browsing and some embedded webviews throw on localStorage rather
    // than returning null. A preference is never worth a blank screen.
    return DEFAULT_TEXT_SIZE;
  }
}

/** Put it on `<html>` and remember it. Called before render and on change. */
export function applyTextSize(size: TextSize): void {
  document.documentElement.setAttribute(ATTRIBUTE, size);
  try {
    window.localStorage.setItem(STORAGE_KEY, size);
  } catch {
    // Applied for this session even if it cannot be remembered for the next.
  }
}

/** Read it back at boot, before React mounts. */
export function initTextSize(): void {
  document.documentElement.setAttribute(ATTRIBUTE, getStoredTextSize());
}

export function useTextSize(): { size: TextSize; setSize: (size: TextSize) => void } {
  const [size, setSizeState] = useState<TextSize>(() => getStoredTextSize());

  // Another tab changing it should not leave this one disagreeing with `<html>`.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = isTextSize(event.newValue) ? event.newValue : DEFAULT_TEXT_SIZE;
      setSizeState(next);
      document.documentElement.setAttribute(ATTRIBUTE, next);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setSize = useCallback((next: TextSize) => {
    setSizeState(next);
    applyTextSize(next);
  }, []);

  return { size, setSize };
}
